import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../../components/ui/accordion'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import {
  CheckCircle2,
  XCircle,
  Trophy,
  Target,
  Users,
  FileText,
  Laptop,
  AlertCircle,
  PlayCircle,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  Star,
  Award,
  Sparkles,
  Calendar,
  MapPin,
  TrendingUp,
  Shield,
  Zap,
  BookOpen,
  Lightbulb,
  Clock,
  Building2,
  Rocket,
  DollarSign
} from 'lucide-react'

export default function DemoGuidelines() {
  const [activeSection, setActiveSection] = useState(null)

  const evaluationCriteria = [
    {
      parameter: 'Business Impact',
      weightage: 20,
      type: 'high',
      icon: <TrendingUp className="h-4 w-4" />,
      details: 'Market size, problem significance, potential to transform the industry, and addressable market opportunity.'
    },
    {
      parameter: 'Scalability',
      weightage: 20,
      type: 'high',
      icon: <Rocket className="h-4 w-4" />,
      details: 'Growth potential, technical architecture, ability to scale operations, and expansion capabilities.'
    },
    {
      parameter: 'Traction',
      weightage: 20,
      type: 'high',
      icon: <TrendingUp className="h-4 w-4" />,
      details: 'User adoption metrics, revenue growth, customer testimonials, and market validation evidence.'
    },
    {
      parameter: 'Solution Exclusivity',
      weightage: 10,
      type: 'medium',
      icon: <Shield className="h-4 w-4" />,
      details: 'Competitive advantage, unique value proposition, intellectual property, and differentiation factors.'
    },
    {
      parameter: 'Future Roadmap',
      weightage: 10,
      type: 'medium',
      icon: <Zap className="h-4 w-4" />,
      details: 'Product development plans, innovation pipeline, strategic vision, and growth strategy.'
    },
    {
      parameter: 'Founder Experience',
      weightage: 10,
      type: 'low',
      icon: <Users className="h-4 w-4" />,
      details: 'Team expertise, industry knowledge, track record, and leadership capabilities.'
    },
    {
      parameter: 'Product Demo',
      weightage: 10,
      type: 'low',
      icon: <Laptop className="h-4 w-4" />,
      details: 'Presentation quality, product demonstration, clarity of communication, and audience engagement.'
    }
  ]

  const pitchFrameworkSections = [
    {
      number: 1,
      title: 'Problem and Context',
      summary: 'Define the critical pain point you\'re solving',
      icon: <AlertCircle className="h-4 w-4" />,
      questions: [
        'What specific problem are you solving?',
        'Who experiences this problem most acutely?',
        'What\'s the cost of not solving this problem?'
      ],
      tip: 'Start with a compelling story or statistic that resonates with your audience.'
    },
    {
      number: 2,
      title: 'Solution / FinTech Product',
      summary: 'Showcase your innovative solution',
      icon: <Lightbulb className="h-4 w-4" />,
      questions: [
        'How does your product work?',
        'What makes your solution unique?',
        'What technology powers your product?'
      ],
      tip: 'Show, don\'t just tell. Use screenshots or a recorded video of your actual product.'
    },
    {
      number: 3,
      title: 'Target Audience',
      summary: 'Identify your ideal customer segments',
      icon: <Users className="h-4 w-4" />,
      questions: [
        'Who are your primary customers?',
        'What are their demographics and behaviors?',
        'How large is your addressable market?'
      ],
      tip: 'Be specific with TAM, SAM, and SOM numbers to show market understanding.'
    },
    {
      number: 4,
      title: 'Competitive Differentiation',
      summary: 'Demonstrate your unique value proposition',
      icon: <Shield className="h-4 w-4" />,
      questions: [
        'Who are your main competitors?',
        'What\'s your unique value proposition?',
        'Why will customers choose you over alternatives?'
      ],
      tip: 'Use a comparison matrix to clearly show your advantages.'
    },
    {
      number: 5,
      title: 'Business Model',
      summary: 'Explain how you make money',
      icon: <DollarSign className="h-4 w-4" />,
      questions: [
        'What are your revenue streams?',
        'What\'s your pricing strategy?',
        'What are your unit economics?'
      ],
      tip: 'Show clear path to profitability with realistic projections.'
    },
    {
      number: 6,
      title: 'Traction & Milestones',
      summary: 'Prove market validation and momentum',
      icon: <TrendingUp className="h-4 w-4" />,
      questions: [
        'What are your key metrics to date?',
        'What milestones have you achieved?',
        'What customer testimonials can you share?'
      ],
      tip: 'Use graphs to visualize growth and include real customer quotes.'
    },
    {
      number: 7,
      title: 'Growth Strategy',
      summary: 'Outline your expansion plans',
      icon: <Rocket className="h-4 w-4" />,
      questions: [
        'How will you acquire customers?',
        'What are your marketing channels?',
        'How will you scale operations?'
      ],
      tip: 'Show a clear 18-24 month roadmap with measurable goals.'
    },
    {
      number: 8,
      title: 'Team',
      summary: 'Introduce your founding team',
      icon: <Users className="h-4 w-4" />,
      questions: [
        'Who are the key team members?',
        'What are their relevant experiences?',
        'Why are you the right team for this?'
      ],
      tip: 'Keep it brief but highlight relevant expertise and achievements.'
    },
    {
      number: 9,
      title: 'Financial Projections',
      summary: 'Present your financial outlook',
      icon: <TrendingUp className="h-4 w-4" />,
      questions: [
        'What are your revenue projections?',
        'When will you break even?',
        'What are your key financial metrics?'
      ],
      tip: 'Be realistic and ready to defend your assumptions.'
    },
    {
      number: 10,
      title: 'Ask',
      summary: 'State your funding requirements',
      icon: <DollarSign className="h-4 w-4" />,
      questions: [
        'How much are you raising?',
        'Use of funds (18–24 months)',
        'Milestones and KPIs you will achieve'
      ],
      tip: 'Be specific about fund usage and measurable outcomes.',
      isFundingSpecific: true
    }
  ]

  const technicalRequirements = [
    { icon: <Laptop className="h-5 w-5" />, text: 'Bring your own laptop for the presentation', type: 'primary' },
    { icon: <FileText className="h-5 w-5" />, text: 'HDMI connector will be provided at the venue', type: 'info' },
    { icon: <AlertCircle className="h-5 w-5" />, text: 'Any additional adapters (USB-C, Thunderbolt, etc.) must be brought by the presenter', type: 'warning' },
    { icon: <CheckCircle2 className="h-5 w-5" />, text: 'Test your laptop connection before the demo', type: 'success' },
    { icon: <AlertCircle className="h-5 w-5" />, text: 'Have a backup plan (cloud presentation, mobile hotspot)', type: 'warning' },
    { icon: <CheckCircle2 className="h-5 w-5" />, text: 'Ensure your laptop is fully charged', type: 'success' }
  ]

  const dos = [
    'Start with a compelling problem statement',
    'Show actual product screenshots or a recorded video',
    'Include real customer testimonials and metrics',
    'Clearly explain your business model',
    'Demonstrate traction with concrete numbers',
    'Keep slides clean and visually appealing',
    'Practice your timing (7 minutes maximum)',
    'Show your competitive advantage clearly'
  ]

  const donts = [
    'Don\'t exceed the time limit',
    'Don\'t use too much technical jargon',
    'Don\'t show mockups instead of real products',
    'Don\'t focus only on features, show benefits',
    'Don\'t use tiny fonts or cluttered slides',
    'Don\'t rely on internet connectivity at the venue',
    'Don\'t be vague about financial numbers',
    'Do not share any confidential data',
    'Don\'t include sales contact information or pricing discussions'
  ]

  const getWeightageColor = (type) => {
    switch(type) {
      case 'high': return 'text-red-600 bg-red-50'
      case 'medium': return 'text-orange-600 bg-orange-50'
      case 'low': return 'text-purple-600 bg-purple-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Demo Guidelines</h1>
        <p className="text-gray-600">
          Your comprehensive guide to delivering a winning demo presentation at IFTA 2025
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>Aurika, Mumbai</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>December 10, 2025</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>7 Minutes Per Demo</span>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Demo Duration', value: '7 min + 3 min Q&A', icon: <Clock className="h-5 w-5" /> },
          { label: 'Criteria', value: '7', icon: <Target className="h-5 w-5" /> },
          { label: 'Sections', value: '10', icon: <FileText className="h-5 w-5" /> },
          { label: 'Awards', value: '2', icon: <Trophy className="h-5 w-5" /> }
        ].map((stat, idx) => (
          <Card key={idx} className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </div>
                <div className="text-green-600">
                  {stat.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Evaluation Criteria */}
      <Accordion type="single" collapsible className="w-full space-y-4">
        <AccordionItem value="evaluation" className="border-0">
          <Card className="bg-gray-50 border-gray-200">
            <AccordionTrigger className="hover:no-underline px-6 py-4">
              <div className="flex items-center gap-3">
                <Target className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold">Evaluation Criteria</h2>
                <Badge className="bg-gray-100 text-gray-700 border-gray-300">100%</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {evaluationCriteria.map((criteria, index) => (
                    <div key={index} className="flex items-start gap-4 p-3 bg-white rounded-lg">
                      <div className="text-green-600 mt-0.5">{criteria.icon}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-gray-900">{criteria.parameter}</h4>
                          <Badge className={getWeightageColor(criteria.type)}>
                            {criteria.weightage}%
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{criteria.details}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* IFTA Startup Pitch Framework */}
        <AccordionItem value="framework" className="border-0">
          <Card className="bg-gray-50 border-gray-200">
            <AccordionTrigger className="hover:no-underline px-6 py-4">
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold">IFTA Startup Pitch Framework</h2>
                <Badge className="bg-gray-100 text-gray-700 border-gray-300">10 Sections</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  {pitchFrameworkSections.map((section) => (
                    <div key={section.number} className="bg-white rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-700 rounded-full flex items-center justify-center font-semibold text-sm">
                          {section.number}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {section.icon}
                            <h3 className="font-semibold text-gray-900">{section.title}</h3>
                            {section.isFundingSpecific && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">If Raising</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{section.summary}</p>
                          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Key Questions:</p>
                            <ul className="space-y-1">
                              {section.questions.map((question, idx) => (
                                <li key={idx} className="text-sm text-gray-600 flex items-start">
                                  <ChevronRight className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0 text-gray-500" />
                                  {question}
                                </li>
                              ))}
                            </ul>
                            <div className="pt-2 border-t">
                              <p className="text-xs text-gray-700">
                                <span className="font-semibold">💡 Tip:</span> {section.tip}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Presentation Rules */}
        <AccordionItem value="rules" className="border-0">
          <Card className="bg-gray-50 border-gray-200">
            <AccordionTrigger className="hover:no-underline px-6 py-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold">Presentation Rules</h2>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-amber-900 mb-1">Important Notice</p>
                        <p className="text-sm text-amber-700 mb-2">
                          Live demos are not allowed. Please prepare a recorded video of your product demonstration.
                        </p>
                        <p className="text-sm text-amber-700">
                          <strong>Note:</strong> Do not include sales contact information or pricing discussions during your presentation. Focus on your product innovation and technology.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Format Requirements</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-600">Maximum 7 minutes presentation time</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-600">PowerPoint or PDF format preferred</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-600">16:9 aspect ratio recommended</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-600">Minimum font size: 18pt</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Content Guidelines</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-600">Include company logo on all slides</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-600">Use high-quality images and graphics</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-600">Include slide numbers</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-600">End with company website (no sales contacts or pricing)</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Technical Requirements */}
        <AccordionItem value="technical" className="border-0">
          <Card className="bg-gray-50 border-gray-200">
            <AccordionTrigger className="hover:no-underline px-6 py-4">
              <div className="flex items-center gap-3">
                <Laptop className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold">Technical Requirements</h2>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {technicalRequirements.map((req, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        req.type === 'primary' ? 'bg-blue-50 border border-blue-200' :
                        req.type === 'success' ? 'bg-green-50 border border-green-200' :
                        req.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                        'bg-sky-50 border border-sky-200'
                      }`}
                    >
                      <div className={
                        req.type === 'primary' ? 'text-blue-600' :
                        req.type === 'success' ? 'text-green-600' :
                        req.type === 'warning' ? 'text-amber-600' :
                        'text-sky-600'
                      }>
                        {req.icon}
                      </div>
                      <span className="text-sm text-gray-700">{req.text}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Pre-Recording Your Product Video */}
        <AccordionItem value="video-recording" className="border-0">
          <Card className="bg-gray-50 border-gray-200">
            <AccordionTrigger className="hover:no-underline px-6 py-4">
              <div className="flex items-center gap-3">
                <PlayCircle className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold">Pre-Recording Your Product Video</h2>
                <Badge className="bg-blue-100 text-blue-700 border-blue-300">Essential</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  {/* Recording Laptop Screens */}
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Laptop className="h-4 w-4 text-gray-700" />
                      Recording Laptop Screens
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="font-medium text-gray-700 min-w-[100px]">Tools:</div>
                        <div className="text-gray-600">
                          <span className="font-semibold">Zoom</span> or <span className="font-semibold">Loom</span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="font-medium text-gray-700 min-w-[100px]">Best Practice:</div>
                        <div className="text-gray-600">
                          Use sample data instead of real customer information. Edit out buffering, typing mistakes, and delays to create a clean ~3 minute video.
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="font-medium text-gray-700 min-w-[100px]">Audio:</div>
                        <div className="text-gray-600">
                          Record your voice explaining the screens, or create a silent video to narrate live on stage.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recording Mobile Apps */}
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Laptop className="h-4 w-4 text-gray-700" />
                      Recording Mobile App Screens
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="font-medium text-gray-700 min-w-[100px]">For iPhone:</div>
                        <div className="text-gray-600">
                          <span className="font-semibold">QuickTime</span> - Built-in Mac application for screen mirroring
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="font-medium text-gray-700 min-w-[100px]">For Android:</div>
                        <div className="text-gray-600">
                          <span className="font-semibold">Vysor</span> - Screen mirroring application for Android devices
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Audio Quality */}
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-gray-700" />
                      Audio Quality Enhancement
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="font-medium text-gray-700 min-w-[100px]">Noise Removal:</div>
                        <div className="text-gray-600">
                          <span className="font-semibold">krisp.ai</span> - Removes background noise for clear audio when using Zoom
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Video Format Requirements */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Video Format Requirements
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        <span className="text-blue-700"><strong>Quality:</strong> HD (1920x1080)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        <span className="text-blue-700"><strong>Aspect Ratio:</strong> 16:9</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        <span className="text-blue-700"><strong>Format:</strong> MP4</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        <span className="text-blue-700"><strong>Duration:</strong> ~3 minutes</span>
                      </div>
                    </div>
                  </div>

                  {/* Pro Tips */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-800">
                      <strong>💡 Pro Tip:</strong> Test your video on different devices before the event. Ensure it plays smoothly without any codec issues. Keep a backup on USB drive and cloud storage.
                    </p>
                  </div>
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Do's and Don'ts */}
        <AccordionItem value="dos-donts" className="border-0">
          <Card className="bg-gray-50 border-gray-200">
            <AccordionTrigger className="hover:no-underline px-6 py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold">Do's and Don'ts</h2>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5" />
                      Do's
                    </h3>
                    <ul className="space-y-2">
                      {dos.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                      <XCircle className="h-5 w-5" />
                      Don'ts
                    </h3>
                    <ul className="space-y-2">
                      {donts.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Quick Reminders */}
        <AccordionItem value="reminders" className="border-0">
          <Card className="bg-gray-50 border-gray-200">
            <AccordionTrigger className="hover:no-underline px-6 py-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold">Quick Reminders</h2>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { icon: <Clock className="h-5 w-5" />, title: 'Time Management', text: '7 minutes strict limit' },
                    { icon: <Users className="h-5 w-5" />, title: 'Audience', text: 'Investors and industry experts' },
                    // { icon: <Trophy className="h-5 w-5" />, title: 'Awards', text: 'Best Demo & People\'s Choice' },
                    { icon: <FileText className="h-5 w-5" />, title: 'Backup', text: 'Keep presentation on USB drive' },
                    { icon: <Laptop className="h-5 w-5" />, title: 'Equipment', text: 'Test all adapters beforehand' },
                    { icon: <AlertCircle className="h-5 w-5" />, title: 'Network', text: 'Don\'t rely on WiFi at venue' }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 text-center">
                      <div className="text-green-600 mb-2 flex justify-center">{item.icon}</div>
                      <h4 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h4>
                      <p className="text-xs text-gray-600">{item.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Evaluation Process */}
        <AccordionItem value="process" className="border-0">
          <Card className="bg-gray-50 border-gray-200">
            <AccordionTrigger className="hover:no-underline px-6 py-4">
              <div className="flex items-center gap-3">
                <Award className="h-5 w-5 text-green-600" />
                <h2 className="text-xl font-semibold">Evaluation Process & Awards</h2>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Judging Process</h3>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">1</div>
                        <p className="text-sm text-gray-600">Each company presents for 7 minutes to a panel of expert judges + 3 minutes for questions from the jury</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">2</div>
                        <p className="text-sm text-gray-600">Judges evaluate based on the 7 criteria with respective weightages</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">3</div>
                        <p className="text-sm text-gray-600">Scores are aggregated and winners are determined</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    {/* <h3 className="font-semibold text-gray-900 mb-3">Award Categories</h3> */}
                    {/* <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Trophy className="h-5 w-5 text-amber-600" />
                          <h4 className="font-semibold text-amber-900">Best Demo Award</h4>
                        </div>
                        <p className="text-sm text-amber-700">
                          Awarded to the company with the highest overall score from judges
                        </p>
                      </div> */}
                      {/* <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Star className="h-5 w-5 text-purple-600" />
                          <h4 className="font-semibold text-purple-900">People's Choice Award</h4>
                        </div>
                        <p className="text-sm text-purple-700">
                          Voted by the audience members attending the event
                        </p>
                      </div> */}
                    {/* </div> */}
                  </div>
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>
      </Accordion>

      {/* Sample Demo Videos */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-green-600" />
            View Sample Demo Videos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Watch examples of successful startup demos to understand the format and flow
          </p>
          <Button
            className="w-full sm:w-auto"
            onClick={() => window.open('https://www.youtube.com/watch?v=Kg06ogskb9I&list=PL8QdgLrZJblICHLGhNeEG4r274lpECYVv', '_blank')}
          >
            <PlayCircle className="h-4 w-4 mr-2" />
            Watch Sample Demos
            <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {/* Need Help Section */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-3">
                If you have any questions about the demo format, technical requirements, or evaluation process, our team is here to help.
              </p>
              <Button
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => window.location.href = '/company/help'}
              >
                Contact Support
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}